import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { STYLE_META, fmtTime } from "@/lib/site";

const EMPTY = {
  name: "",
  style: "Ballet",
  day: "Monday",
  start_time: "16:00",
  end_time: "16:45",
  age_group: "",
  venue: "The Umbrella Centre",
  level: "",
  notes: "",
};

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function AdminTimetable() {
  const [classes, setClasses] = useState(null);
  const [meta, setMeta] = useState({ days: DAY_ORDER, styles: Object.keys(STYLE_META), venues: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api
      .get("/timetable")
      .then((res) => setClasses(res.data))
      .catch(() => {
        setClasses([]);
        toast.error("Couldn't load timetable");
      });
  };

  useEffect(() => {
    load();
    api
      .get("/timetable/meta")
      .then((res) => setMeta(res.data))
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (cls) => {
    setEditing(cls);
    setForm({
      name: cls.name,
      style: cls.style,
      day: cls.day,
      start_time: cls.start_time,
      end_time: cls.end_time,
      age_group: cls.age_group || "",
      venue: cls.venue || "",
      level: cls.level || "",
      notes: cls.notes || "",
    });
    setOpen(true);
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Please add a class name");
      return;
    }
    if (!form.start_time || !form.end_time) {
      toast.error("Please set start and end times");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/timetable/${editing.id}`, form);
        toast.success("Class updated");
      } else {
        await api.post("/timetable", form);
        toast.success("Class added to the timetable");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/timetable/${deleting.id}`);
      toast.success("Class removed");
      setDeleting(null);
      load();
    } catch {
      toast.error("Couldn't delete class");
    }
  };

  return (
    <div data-testid="admin-timetable-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Timetable</h1>
          <p className="text-muted-foreground mt-1">Changes appear on the public timetable straight away.</p>
        </div>
        <Button onClick={openCreate} className="rounded-[4px] font-semibold w-fit" data-testid="admin-timetable-create-button">
          <Plus className="h-4 w-4 mr-1.5" /> Add class
        </Button>
      </div>

      <Card className="rounded-[4px] mt-6 overflow-hidden">
        <CardContent className="p-0">
          {classes === null ? (
            <div className="p-6 space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-14 rounded-[4px]" />
              ))}
            </div>
          ) : classes.length === 0 ? (
            <p className="p-10 text-center text-muted-foreground" data-testid="admin-timetable-empty">
              No classes yet — add your first one!
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead>Day</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead className="hidden md:table-cell">Style</TableHead>
                  <TableHead className="hidden md:table-cell">Ages</TableHead>
                  <TableHead className="hidden lg:table-cell">Venue</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {classes.map((c) => (
                  <TableRow key={c.id} data-testid="admin-timetable-row">
                    <TableCell className="font-medium">{c.day}</TableCell>
                    <TableCell className="tabular-nums whitespace-nowrap">
                      {fmtTime(c.start_time)}&ndash;{fmtTime(c.end_time)}
                    </TableCell>
                    <TableCell className="font-medium max-w-[200px] truncate">{c.name}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge variant="outline" className={`rounded-[4px] ${STYLE_META[c.style]?.badge || ""}`}>{c.style}</Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{c.age_group}</TableCell>
                    <TableCell className="hidden lg:table-cell text-muted-foreground max-w-[180px] truncate">{c.venue}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} aria-label={`Edit ${c.name}`} data-testid="admin-timetable-edit-button">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleting(c)}
                          aria-label={`Delete ${c.name}`}
                          className="text-destructive hover:text-destructive"
                          data-testid="admin-timetable-delete-button"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-card">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit class" : "Add a class"}</DialogTitle>
            <DialogDescription>Fill in the details — the public timetable updates instantly.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="class-name">Class name</Label>
              <Input id="class-name" value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="e.g. Grade 2 Ballet" data-testid="admin-timetable-name-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Style</Label>
                <Select value={form.style} onValueChange={(v) => setField("style", v)}>
                  <SelectTrigger data-testid="admin-timetable-style-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {meta.styles.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Day</Label>
                <Select value={form.day} onValueChange={(v) => setField("day", v)}>
                  <SelectTrigger data-testid="admin-timetable-day-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(meta.days || DAY_ORDER).map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="class-start">Start time</Label>
                <Input id="class-start" type="time" value={form.start_time} onChange={(e) => setField("start_time", e.target.value)} data-testid="admin-timetable-start-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="class-end">End time</Label>
                <Input id="class-end" type="time" value={form.end_time} onChange={(e) => setField("end_time", e.target.value)} data-testid="admin-timetable-end-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="class-ages">Age group</Label>
                <Input id="class-ages" value={form.age_group} onChange={(e) => setField("age_group", e.target.value)} placeholder="e.g. 6–8 yrs" data-testid="admin-timetable-ages-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="class-level">Level (optional)</Label>
                <Input id="class-level" value={form.level} onChange={(e) => setField("level", e.target.value)} placeholder="e.g. ISTD Grade 2" data-testid="admin-timetable-level-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Venue</Label>
              <Select value={form.venue} onValueChange={(v) => setField("venue", v)}>
                <SelectTrigger data-testid="admin-timetable-venue-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(meta.venues.length ? meta.venues : ["The Umbrella Centre", "Whitstable Swimming Pool – Dance Studio", "Treetops Dance Studio"]).map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="class-notes">Notes (optional)</Label>
              <Textarea id="class-notes" rows={2} value={form.notes} onChange={(e) => setField("notes", e.target.value)} data-testid="admin-timetable-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-[4px]">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="rounded-[4px] font-semibold" data-testid="admin-timetable-save-button">
              {saving ? "Saving…" : editing ? "Save changes" : "Add class"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this class?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleting?.name}” ({deleting?.day}) will be removed from the public timetable.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-[4px]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-[4px] bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-timetable-confirm-delete-button">
              Remove class
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
